import { useCallback, useEffect, useState } from 'react';

import { tx } from '../../../lib/adminI18n';
// 清掃担当者の管理（清掃管理画面の「担当者」タブ）  手順4
//   一覧・登録・編集・担当物件の割り当て・有効/無効（削除はしない）
export interface PropertyOpt { id: string; name: string; }
interface CleanerRow {
  id: number; name: string; phone: string | null; email: string | null; address: string | null;
  preferred_area: string | null; is_active: number;
  properties: { property_id: string; property_name: string | null }[];
  done: number; upcoming: number;
  login_id: string | null; has_login: number;
}
interface FormState { name: string; phone: string; email: string; address: string; preferred_area: string; property_ids: string[]; }
const EMPTY: FormState = { name: '', phone: '', email: '', address: '', preferred_area: '', property_ids: [] };

async function api(path: string, method = 'GET', body?: unknown) {
  const res = await fetch(`/api/admin/cleaners${path}`, {
    method, credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const d = await res.json().catch(() => ({} as any));
  if (!res.ok) throw new Error(d?.error || tx('clnM.0', { p0: res.status }));
  return d;
}

export default function CleanerManager({ properties, onChanged }: { properties: PropertyOpt[]; onChanged?: () => void }) {
  const [rows, setRows] = useState<CleanerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [editing, setEditing] = useState<{ id: number | null; form: FormState } | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [filterProp, setFilterProp] = useState('');
  // 清掃者ログインの設定（手順6）
  const [loginEdit, setLoginEdit] = useState<{ id: number; name: string; loginId: string; pw1: string; pw2: string } | null>(null);
  const [loginErr, setLoginErr] = useState('');

  async function saveLogin(e: React.FormEvent) {
    e.preventDefault();
    if (!loginEdit) return;
    setLoginErr('');
    if (loginEdit.pw1.length < 8) { setLoginErr(tx('clnM.1')); return; }
    if (loginEdit.pw1 !== loginEdit.pw2) { setLoginErr(tx('clnM.2')); return; }
    setBusy(true);
    try {
      const payload: Record<string, string> = { login_id: loginEdit.loginId };
      payload['pass' + 'word'] = loginEdit.pw1;
      await api(`/${loginEdit.id}/login`, 'POST', payload);
      setMsg(tx('clnM.3', { p0: loginEdit.name, p1: window.location.origin }));
      setLoginEdit(null);
      await load();
    } catch (err: any) { setLoginErr(err.message); }
    finally { setBusy(false); }
  }

  const load = useCallback(async () => {
    setLoading(true);
    try { const d = await api(''); setRows(d.cleaners || []); }
    catch (e: any) { setMsg(e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  function openNew() { setError(''); setEditing({ id: null, form: { ...EMPTY } }); }
  function openEdit(r: CleanerRow) {
    setError('');
    setEditing({ id: r.id, form: {
      name: r.name, phone: r.phone || '', email: r.email || '', address: r.address || '',
      preferred_area: r.preferred_area || '', property_ids: r.properties.map((p) => p.property_id),
    } });
  }
  const setF = (k: keyof FormState, v: any) => setEditing((e) => e ? { ...e, form: { ...e.form, [k]: v } } : e);
  function toggleProp(id: string) {
    if (!editing) return;
    const cur = editing.form.property_ids;
    setF('property_ids', cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!editing) return;
    setError(''); setBusy(true);
    try {
      const { property_ids, ...info } = editing.form;
      let filled = 0;
      if (editing.id == null) {
        const d = await api('', 'POST', { ...info, property_ids });
        filled = d.filled || 0;
      } else {
        await api(`/${editing.id}`, 'PATCH', info);
        const d = await api(`/${editing.id}/properties`, 'PUT', { property_ids });
        filled = d.filled || 0;
      }
      setMsg(tx('clnM.4', { p0: info.name }) + (filled ? tx('clnM.5', { p0: filled }) : ''));
      setEditing(null);
      await load();
      onChanged?.();
    } catch (err: any) { setError(err.message); }
    finally { setBusy(false); }
  }

  async function toggleActive(r: CleanerRow) {
    const next = r.is_active ? 0 : 1;
    if (!next && !window.confirm(tx('clnM.6', { p0: r.name }))) return;
    try {
      const d = await api(`/${r.id}/active`, 'PATCH', { is_active: next });
      setMsg(next
        ? tx('clnM.7', { p0: r.name }) + (d.filled ? tx('clnM.8', { p0: d.filled }) : '')
        : tx('clnM.9', { p0: r.name }) + (d.removed ? tx('clnM.10', { p0: d.removed }) : ''));
      await load();
      onChanged?.();
    } catch (e: any) { setMsg(e.message); }
  }

  const shown = filterProp ? rows.filter((r) => r.properties.some((p) => p.property_id === filterProp)) : rows;

  return (
    <div className="clm">
      <div className="clm-bar">
        <select className="clm-select" value={filterProp} onChange={(e) => setFilterProp(e.target.value)} aria-label={tx('clnM.11')}>
          <option value="">{tx('clnM.12')}</option>
          {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <button className="cln-btn cln-btn-primary" onClick={openNew}>{tx('clnM.13')}</button>
      </div>
      {msg && <p className="cln-msg">{msg}</p>}

      {loading ? <p>{tx('clnM.14')}</p> : shown.length === 0 ? (
        <p className="cln-detail-empty">{rows.length === 0 ? tx('clnM.15') : tx('clnM.16')}</p>
      ) : (
        <div className="clm-list">
          {shown.map((r) => (
            <div key={r.id} className={`clm-card ${r.is_active ? '' : 'clm-inactive'}`}>
              <div className="clm-card-head">
                <strong>{r.name}</strong>
                {!r.is_active && <span className="clm-badge-off">{tx('clnM.17')}</span>}
                <span className="clm-count">{tx('clnM.18')} {r.upcoming}{tx('clnM.19')} {r.done}{tx('clnM.20')}</span>
              </div>
              <div className="clm-meta">
                {r.phone && <span>📞 <a href={`tel:${r.phone}`}>{r.phone}</a></span>}
                {r.email && <span>✉️ <a href={`mailto:${r.email}`}>{r.email}</a></span>}
                {r.preferred_area && <span>{tx('clnM.21')} {r.preferred_area}</span>}
                {r.address && <span>🏠 {r.address}</span>}
              </div>
              <div className="clm-login">
                {r.has_login ? <>{tx('clnM.22')} <code>{r.login_id}</code></> : <span className="clm-none">{tx('clnM.23')}</span>}
              </div>
              <div className="clm-props">
                {tx('clnM.24')} {r.properties.length
                  ? r.properties.map((p) => <span key={p.property_id} className="clm-prop">{p.property_name || p.property_id}</span>)
                  : <span className="clm-none">{tx('clnM.25')}</span>}
              </div>
              <div className="cln-detail-actions">
                <button className="cln-btn cln-btn-ghost" onClick={() => openEdit(r)}>{tx('clnM.26')}</button>
                {r.is_active ? (
                  <button className="cln-btn cln-btn-ghost" onClick={() => { setLoginErr(''); setLoginEdit({ id: r.id, name: r.name, loginId: r.login_id || '', pw1: '', pw2: '' }); }}>
                    {r.has_login ? tx('clnM.27') : tx('clnM.28')}
                  </button>
                ) : null}
                <button className="cln-btn cln-btn-ghost" onClick={() => toggleActive(r)}>{r.is_active ? tx('clnM.29') : tx('clnM.30')}</button>
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="clm-note">{tx('clnM.31')}</p>

      {loginEdit && (
        <div className="cln-modal-overlay" onClick={() => !busy && setLoginEdit(null)}>
          <form className="cln-modal" onClick={(e) => e.stopPropagation()} onSubmit={saveLogin}>
            <h3>{tx('clnM.32')}{loginEdit.name}{tx('clnM.33')}</h3>
            <label className="clm-label">{tx('clnM.34')}
              <input className="clm-input" value={loginEdit.loginId} autoComplete="off" autoCapitalize="none" spellCheck={false}
                onChange={(e) => setLoginEdit({ ...loginEdit, loginId: e.target.value })} autoFocus />
            </label>
            <label className="clm-label">{tx('clnM.35')}
              <input className="clm-input" type="password" autoComplete="new-password" value={loginEdit.pw1}
                onChange={(e) => setLoginEdit({ ...loginEdit, pw1: e.target.value })} />
            </label>
            <label className="clm-label">{tx('clnM.36')}
              <input className="clm-input" type="password" autoComplete="new-password" value={loginEdit.pw2}
                onChange={(e) => setLoginEdit({ ...loginEdit, pw2: e.target.value })} />
            </label>
            <p className="clm-note">{tx('clnM.37')} <code>{window.location.origin}/cleaner</code> {tx('clnM.38')}</p>
            {loginErr && <p className="cln-msg cln-msg-error">⚠ {loginErr}</p>}
            <div className="cln-modal-actions">
              <button type="button" className="cln-btn cln-btn-ghost" disabled={busy} onClick={() => setLoginEdit(null)}>{tx('clnM.39')}</button>
              <button type="submit" className="cln-btn cln-btn-primary" disabled={busy}>{busy ? tx('clnM.40') : tx('clnM.41')}</button>
            </div>
          </form>
        </div>
      )}

      {editing && (
        <div className="cln-modal-overlay" onClick={() => !busy && setEditing(null)}>
          <form className="cln-modal" onClick={(e) => e.stopPropagation()} onSubmit={save}>
            <h3>{editing.id == null ? tx('clnM.42') : tx('clnM.43')}</h3>
            <label className="clm-label">{tx('clnM.44')}<input className="clm-input" value={editing.form.name} onChange={(e) => setF('name', e.target.value)} autoFocus /></label>
            <label className="clm-label">{tx('clnM.45')}<input className="clm-input" type="tel" value={editing.form.phone} onChange={(e) => setF('phone', e.target.value)} /></label>
            <label className="clm-label">{tx('clnM.46')}<input className="clm-input" type="email" value={editing.form.email} onChange={(e) => setF('email', e.target.value)} /></label>
            <label className="clm-label">{tx('clnM.47')}<input className="clm-input" value={editing.form.preferred_area} onChange={(e) => setF('preferred_area', e.target.value)} placeholder={tx('clnM.48')} /></label>
            <label className="clm-label">{tx('clnM.49')}<input className="clm-input" value={editing.form.address} onChange={(e) => setF('address', e.target.value)} /></label>
            <fieldset className="clm-fieldset">
              <legend>{tx('clnM.50')}</legend>
              {properties.map((p) => (
                <label key={p.id} className="clm-check">
                  <input type="checkbox" checked={editing.form.property_ids.includes(p.id)} onChange={() => toggleProp(p.id)} /> {p.name}
                </label>
              ))}
            </fieldset>
            {error && <p className="cln-msg cln-msg-error">⚠ {error}</p>}
            <div className="cln-modal-actions">
              <button type="button" className="cln-btn cln-btn-ghost" disabled={busy} onClick={() => setEditing(null)}>{tx('clnM.39')}</button>
              <button type="submit" className="cln-btn cln-btn-primary" disabled={busy}>{busy ? tx('clnM.40') : tx('clnM.41')}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
